<template>
  <NuxtLink
    :to="localizedHref"
    class="font-mplus block w-full max-w-sm text-inherit no-underline hover:no-underline focus:no-underline"
    @click="onOpen"
  >
    <article
      ref="cardRef"
      class="cursor-pointer"
      :class="{ 'opacity-60': isCanceled }"
    >
      <div class="relative overflow-hidden rounded-3xl bg-gray-200">
        <div class="aspect-square">
          <div class="relative h-full w-full bg-gray-200">
            <div
              v-if="!imageLoaded"
              class="absolute inset-0 animate-pulse bg-gray-200"
            ></div>
            <img
              ref="imageRef"
              :src="currentImageUrl"
              :alt="eventName"
              class="h-full w-full object-cover transition-opacity duration-300"
              :class="[
                isCanceled ? 'grayscale' : '',
                imageLoaded ? 'opacity-100' : 'opacity-0',
              ]"
              loading="lazy"
              decoding="async"
              width="600"
              height="600"
              @load="onImageLoad"
              @error="onImageError"
            />
          </div>
        </div>

        <div
          v-if="isCanceled"
          class="absolute left-4 top-4 rounded-xl bg-red-600 px-6 py-2 text-sm font-bold tracking-wide text-white shadow"
        >
          {{ $t("eventCard.canceled") }}
        </div>
        <span
          v-else-if="isInProgress"
          class="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 shadow"
        >
          <span
            class="inline-block h-2 w-2 animate-pulse rounded-full bg-green-500"
          ></span>
          {{ $t("event.inProgress") }}
        </span>
        <div
          v-else-if="isToday"
          class="absolute bottom-4 left-4 rounded-xl bg-pink-500 px-6 py-2 text-sm font-bold tracking-wide text-white shadow"
        >
          {{ $t("eventCard.today") }}
        </div>
      </div>

      <!-- ✅ Text content: HARD reset + controlled spacing -->
      <div class="px-1 pt-4">
        <div class="event-stack font-medium">
          <h3
            class="event-title truncate font-rounded font-bold uppercase"
            :class="{ 'text-gray-400 line-through': isCanceled }"
          >
            {{ eventName }}
          </h3>
          <h3 class="text-xs font-medium">
            {{ city?.cityName }}, {{ country?.countryName }}
          </h3>
          <div
            v-if="category && category.length"
            class="mt-3 flex w-full flex-col items-start gap-2 overflow-x-hidden"
          >
            <div
              v-for="(cat, i) in category.slice(0, 2)"
              :key="i"
              class="inline-flex flex-shrink-0 items-center gap-1"
            >
              <Icon class="h-3 w-3 text-purple-950" :name="cat" />
              <span
                class="whitespace-nowrap text-xs font-medium text-purple-950"
                :title="cat"
                >{{ cat }}</span
              >
            </div>
          </div>
          <div class="mt-4 flex w-full flex-1 justify-between text-sm">
            <p class="event-meta truncate text-purple-950">
              {{ formattedDate }}
            </p>
            <p v-if="!isCanceled" class="event-meta truncate text-pink-500">
              {{ formattedPrice }}
            </p>
            <p v-else class="event-meta truncate font-semibold text-red-500">
              {{ $t("eventCard.canceledStatus") }}
            </p>
          </div>
          <p
            v-if="eventTimeZone"
            class="event-meta mt-1 truncate text-xs text-slate-500"
          >
            {{ $t("event.venueTime", { timeZone: eventTimeZone }) }}
          </p>
        </div>
      </div>
    </article>
  </NuxtLink>
</template>

<script setup lang="ts">
import {
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { useAnalytics } from "~/composables/useAnalytics";
import Icon from "~/components/Icon.vue";
import { formatPrice } from "~~/shared/utils/formatPrice";
import type {
  CreateCityResponse,
  CreateCountryResponse,
} from "~~/shared/models";
import {
  eventCalendarDateKey,
  formatEventCalendarDate,
  resolveEventTimeZoneCandidate,
  toEventDate,
} from "~~/shared/utils/seo/eventTime";

type Props = {
  id?: string;
  imageUrl?: string;
  eventName?: string;
  establishmentName?: string;
  category?: string[];
  date?: string | Date;
  start?: string | Date;
  endDate?: string | Date;
  timeZone?: string;
  price?: number;
  currency?: string;
  locale?: string;
  slug?: string; // event slug
  establishmentSlug?: string; // parent establishment slug
  href?: string; // optional explicit href override
  city?: CreateCityResponse;
  country?: CreateCountryResponse;
  isCanceled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  id: "",
  imageUrl: "",
  eventName: "",
  establishmentName: "",
  category: () => [],
  date: () => new Date(),
  start: () => new Date(),
  endDate: () => new Date(),
  timeZone: undefined,
  price: 0,
  currency: "EUR",
  locale: "de-AT",
  slug: "",
  establishmentSlug: "",
  href: "",
  city: undefined,
  country: undefined,
  isCanceled: false,
});

const emit = defineEmits<{ (e: "open", payload: Props): void }>();
const localePath = useLocalePath();
const route = useRoute();
const renderNow = useHydrationClock(`event-cards:${route.path}`);
const { trackViewed, trackClicked } = useAnalytics();
const imageRef = ref<HTMLImageElement | null>(null);
const imageLoaded = ref(false);
const fallbackImageUrl = "/images/placeholder-soft-neutral-event.svg";
const currentImageUrl = ref(fallbackImageUrl);

// IntersectionObserver for view-impression tracking (fire once per card)
const cardRef = ref<HTMLElement | null>(null);
let viewTracked = false;
let observer: IntersectionObserver | null = null;

onMounted(() => {
  syncImageState();
  if (!cardRef.value || !props.id) return;
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !viewTracked && props.id) {
          viewTracked = true;
          trackViewed("event", props.id);
          observer?.disconnect();
        }
      }
    },
    { threshold: 0.5 },
  );
  observer.observe(cardRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

watch(
  () => props.imageUrl,
  async (nextUrl) => {
    imageLoaded.value = false;
    currentImageUrl.value = nextUrl?.trim() || fallbackImageUrl;
    await nextTick();
    syncImageState();
  },
  { immediate: true },
);

function onImageLoad() {
  imageLoaded.value = true;
}

function syncImageState() {
  if (!imageRef.value?.complete) return;
  if (imageRef.value.naturalWidth > 0) imageLoaded.value = true;
  else onImageError();
}

function onImageError() {
  if (currentImageUrl.value !== fallbackImageUrl) {
    imageLoaded.value = false;
    currentImageUrl.value = fallbackImageUrl;
    return;
  }

  imageLoaded.value = true;
}

function normalizeCardDate(d: string | Date): string {
  if (d instanceof Date) return d.toISOString();
  const dmY = /^(\d{2})\.(\d{2})\.(\d{4})$/;
  const m = d.match(dmY);
  if (m) {
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}T00:00:00`;
  }
  return d;
}

const eventTimeZone = computed(() =>
  resolveEventTimeZoneCandidate(props.timeZone),
);
const eventStartRaw = computed(() =>
  normalizeCardDate(props.start ?? props.date),
);
const eventStart = computed(() =>
  eventTimeZone.value
    ? toEventDate(eventStartRaw.value, eventTimeZone.value)
    : null,
);

const isToday = computed(() => {
  if (!eventTimeZone.value) return false;
  const eventDay = eventStart.value
    ? eventCalendarDateKey(eventStart.value, eventTimeZone.value)
    : null;
  const today = eventCalendarDateKey(
    new Date(renderNow.value),
    eventTimeZone.value,
  );
  return Boolean(eventDay && eventDay === today);
});

const isInProgress = computed(() => {
  if (props.isCanceled) return false;
  if (!props.endDate) return false;
  if (!eventTimeZone.value) return false;

  const startMs = eventStart.value?.getTime();
  const endMs = toEventDate(
    normalizeCardDate(props.endDate),
    eventTimeZone.value,
  )?.getTime();
  if (startMs === undefined || endMs === undefined) return false;

  const now = renderNow.value;
  return startMs <= now && now <= endMs;
});

const formattedDate = computed(() => {
  if (!eventTimeZone.value) return String(props.date);
  const startStr = formatEventCalendarDate(
    eventStartRaw.value,
    eventTimeZone.value,
  );
  if (!startStr) return String(props.date);

  if (props.endDate) {
    const endStr = formatEventCalendarDate(
      normalizeCardDate(props.endDate),
      eventTimeZone.value,
    );
    if (endStr && startStr !== endStr) {
      return `${startStr} - ${endStr}`;
    }
  }
  return startStr;
});

const formattedPrice = computed(() =>
  formatPrice(props.price, props.currency, props.locale),
);

function buildHref() {
  if (props.href) return props.href;

  let base = null;
  if (props.establishmentSlug && props.slug) {
    base = `/establishment/${props.establishmentSlug}/event/${props.slug}`;
  }

  if (!base) return null;

  // If an imageUrl is available, include it as a query param so the event page
  // can show a fast client-side fallback image while it fetches full data.
  try {
    if (props.imageUrl) {
      const q = new URLSearchParams({ img: String(props.imageUrl) });
      return `${base}?${q.toString()}`;
    }
  } catch {
    // ignore URLSearchParams failures and return base
  }
  return base;
}

const rawHref = computed(() => buildHref());
const localizedHref = computed(() => localePath(rawHref.value ?? "/"));

function onOpen(event?: Event) {
  emit("open", props);
  if (props.id) trackClicked("event", props.id);

  if (!rawHref.value) {
    event?.preventDefault();
  }
}
</script>
